import { useState, useEffect, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '@clerk/react';
import { jobs, projects } from '../services/api';
import Sidebar from '../components/Sidebar';
import { IconActivity, IconCheck, IconTransfer, IconAlert } from '../components/icons';

const JOB_STATUS = {
  queued:  { label: '● Queued',  color: 'text-muted', bg: 'bg-muted/10' },
  running: { label: '⟳ Running', color: 'text-warn',  bg: 'bg-warn/10' },
  done:    { label: '✓ Done',    color: 'text-good',  bg: 'bg-good/10' },
  failed:  { label: '✗ Failed',  color: 'text-bad',   bg: 'bg-bad/10' },
  stopped: { label: '■ Stopped', color: 'text-dim',   bg: 'bg-dim/10' },
};
const jobMeta = s => JOB_STATUS[s] || JOB_STATUS.queued;

const th = 'border-b border-border2 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-dim';
const td = 'border-b border-border/60 px-3.5 py-2.5 font-mono text-xs';

const selectCls = 'rounded-md border border-border2 bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-accent/50';

export default function Logs() {
  const { orgId } = useAuth();
  const [jobList, setJobList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [live, setLive] = useState(false);

  // Refetch whenever the active Clerk workspace changes (personal <-> org,
  // or switching between orgs) -- every list here is scoped server-side to
  // whichever one is active, see services/authz.js.
  useEffect(() => {
    setLoading(true);
    let byId = {};
    const socket = io();
    socket.on('connect', () => setLive(true));
    socket.on('disconnect', () => setLive(false));
    socket.on('job_update', job => {
      const withName = { ...job, project_name: byId[job.project_id] };
      setJobList(prev => prev.some(j => j.id === job.id)
        ? prev.map(j => j.id === job.id ? withName : j)
        : [withName, ...prev]);
    });

    projects.list().then(r => {
      setProjectList(r.data);
      byId = Object.fromEntries(r.data.map(p => [p.id, p.name]));
      r.data.forEach(p => socket.emit('join_project', p.id));
    });
    jobs.list().then(r => setJobList(r.data)).finally(() => setLoading(false));

    return () => socket.disconnect();
  }, [orgId]);

  const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const secs = Math.round((new Date(end) - new Date(start)) / 1000);
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const formatTime = t => t ? new Date(t).toLocaleString() : '—';

  const filtered = useMemo(() => jobList.filter(j =>
    (statusFilter === 'all' || j.status === statusFilter) &&
    (projectFilter === 'all' || j.project_id === projectFilter)
  ), [jobList, statusFilter, projectFilter]);

  const counts = useMemo(() => ({
    total: jobList.length,
    running: jobList.filter(j => j.status === 'running').length,
    failed: jobList.filter(j => j.status === 'failed').length,
    done: jobList.filter(j => j.status === 'done').length,
  }), [jobList]);

  return (
    <div className="flex h-screen bg-bg font-sans text-ink">
      <Sidebar active="logs" />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex h-14 shrink-0 items-center gap-4 border-b border-border2 px-6">
          <div className="flex-1 font-display text-base font-bold">Logs</div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted">
            <span className={`h-1.5 w-1.5 rounded-full ${live ? 'bg-good motion-safe:animate-pulse' : 'bg-dim'}`} />
            {live ? 'Live' : 'Connecting…'}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="mb-6 grid grid-cols-4 gap-3">
            {[
              ['Total jobs', counts.total, IconActivity, 'text-accent', 'bg-accent/10'],
              ['Running', counts.running, IconTransfer, 'text-warn', 'bg-warn/10'],
              ['Done', counts.done, IconCheck, 'text-good', 'bg-good/10'],
              ['Failed', counts.failed, IconAlert, 'text-bad', 'bg-bad/10'],
            ].map(([label, val, Icon, color, chip]) => (
              <div key={label} className="rounded-xl border border-border2 bg-surface px-5 py-4">
                <div className={`mb-3 flex h-8 w-8 items-center justify-center rounded-lg ${chip} ${color}`}>
                  <Icon />
                </div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-dim">{label}</div>
                <div className={`text-2xl font-semibold ${color}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="mb-3.5 flex items-center gap-2.5">
            <select className={selectCls} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              {Object.keys(JOB_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className={selectCls} value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
              <option value="all">All projects</option>
              {projectList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div className="overflow-hidden rounded-xl border border-border2 bg-surface">
            <div className="flex items-center justify-between border-b border-border2 px-5 py-3.5">
              <span className="text-sm font-bold">Activity</span>
              <span className="font-mono text-xs text-dim">{filtered.length} of {jobList.length} jobs</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] border-collapse">
                <thead>
                  <tr>
                    {['Project', 'Type', 'Status', 'Exit code', 'Started', 'Duration', 'Triggered by'].map(h => (
                      <th key={h} className={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className={`${td} text-muted`} colSpan={7}>Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td className={`${td} text-muted`} colSpan={7}>No jobs match these filters.</td></tr>
                  ) : filtered.map(j => {
                    const meta = jobMeta(j.status);
                    return (
                      <tr key={j.id} className="hover:bg-surface2/50">
                        <td className={td}>{j.project_name || '—'}</td>
                        <td className={td}>{j.type}</td>
                        <td className={td}>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.color} ${meta.bg}`}>{meta.label}</span>
                        </td>
                        <td className={`${td} ${j.exit_code ? 'text-bad' : 'text-muted'}`}>{j.exit_code ?? '—'}</td>
                        <td className={`${td} text-muted`}>{formatTime(j.started_at)}</td>
                        <td className={`${td} text-muted`}>{formatDuration(j.started_at, j.completed_at)}</td>
                        <td className={`${td} text-muted`}>{j.triggered_by ? 'You' : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
